<template>
  <Head title="Account Settings" />
  <div class="mx-auto w-11/12 max-w-[1200px] py-6">
    <!-- Header card -->
    <div
      class="border-t-navy-800 mb-4 overflow-hidden rounded-lg border border-t-[3px] border-neutral-200 bg-white"
    >
      <div class="px-6 py-5">
        <div class="flex items-center gap-3.5">
          <div
            class="from-navy-800 to-navy-700 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br text-base font-bold text-white"
          >
            {{ userInitials }}
          </div>
          <div>
            <h1 class="text-navy-900 text-[22px] font-bold tracking-[-0.01em]">
              {{ user.name }}
            </h1>
            <p class="mt-0.5 text-[13px] text-neutral-500">
              {{ user.email }}
            </p>
          </div>
        </div>

        <!-- Meta row -->
        <div
          class="border-navy-100 mt-4 flex items-center gap-6 border-t pt-3.5"
        >
          <div>
            <div
              class="text-[11px] font-semibold tracking-[0.06em] text-neutral-400 uppercase"
            >
              Two-Factor Auth
            </div>
            <div
              class="mt-0.5 flex items-center gap-1 text-[13px] font-medium"
              :class="user.mfa_enabled ? 'text-green-700' : 'text-amber-600'"
            >
              <span
                class="inline-block h-1.5 w-1.5 rounded-full"
                :class="user.mfa_enabled ? 'bg-green-600' : 'bg-amber-500'"
              />
              {{ user.mfa_enabled ? "Enabled" : "Not enabled" }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-4">
      <!-- Profile card -->
      <AppCard>
        <template #header>
          <div
            class="text-navy-800 flex items-center gap-2 text-sm font-semibold"
          >
            <i class="pi pi-user text-navy-400 text-[13px]" />
            Profile
          </div>
        </template>
        <template #actions>
          <AppButton
            variant="ghost"
            size="sm"
            @click="showProfileDrawer = true"
          >
            Edit
          </AppButton>
        </template>

        <div class="space-y-0">
          <div
            class="flex items-center justify-between border-b border-neutral-100 py-2.5"
          >
            <span class="text-[13px] text-neutral-500">Name</span>
            <span class="text-navy-800 text-[13px] font-medium">
              {{ user.name }}
            </span>
          </div>
          <div class="flex items-center justify-between py-2.5">
            <span class="text-[13px] text-neutral-500">Email</span>
            <span class="text-navy-800 text-[13px] font-medium">
              {{ user.email }}
            </span>
          </div>
        </div>
      </AppCard>

      <!-- Password card -->
      <AppCard>
        <template #header>
          <div
            class="text-navy-800 flex items-center gap-2 text-sm font-semibold"
          >
            <i class="pi pi-lock text-navy-400 text-[13px]" />
            Password
          </div>
        </template>
        <template #actions>
          <AppButton
            variant="ghost"
            size="sm"
            @click="showPasswordDrawer = true"
          >
            Change
          </AppButton>
        </template>

        <div class="flex items-center justify-between py-2.5">
          <span class="text-[13px] text-neutral-500">Password</span>
          <span class="text-navy-800 text-[13px] font-medium">
            ••••••••••••
          </span>
        </div>
      </AppCard>

      <!-- Security card -->
      <AppCard>
        <template #header>
          <div
            class="text-navy-800 flex items-center gap-2 text-sm font-semibold"
          >
            <i class="pi pi-shield text-navy-400 text-[13px]" />
            Security
          </div>
        </template>
        <!-- MFA -->
        <div class="flex items-center justify-between py-1">
          <div>
            <div class="text-navy-800 text-[13px] font-medium">
              Two-Factor Authentication
            </div>
            <div
              class="mt-0.5 flex items-center gap-1 text-[12px] font-medium"
              :class="user.mfa_enabled ? 'text-green-600' : 'text-amber-600'"
            >
              <span
                class="inline-block h-1.5 w-1.5 rounded-full"
                :class="user.mfa_enabled ? 'bg-green-600' : 'bg-amber-500'"
              />
              {{ user.mfa_enabled ? "Enabled" : "Not enabled" }}
            </div>
          </div>
          <Link href="/account/mfa">
            <AppButton variant="secondary" size="sm">
              {{ user.mfa_enabled ? "Manage" : "Set Up" }}
            </AppButton>
          </Link>
        </div>

        <div class="my-3 h-px bg-neutral-100" />

        <!-- Backup codes -->
        <div class="flex items-center justify-between py-1">
          <div>
            <div class="text-navy-800 text-[13px] font-medium">
              Backup Codes
            </div>
            <div class="mt-0.5 text-[12px] text-neutral-400">
              <template v-if="user.mfa_enabled">
                {{ backupCodesRemaining }} of 10 remaining
              </template>
              <template v-else> Enable MFA first </template>
            </div>
          </div>
          <Link v-if="user.mfa_enabled" href="/account/mfa">
            <AppButton variant="secondary" size="sm"> Regenerate </AppButton>
          </Link>
        </div>
      </AppCard>
    </div>

    <!-- Edit Profile Drawer -->
    <Drawer
      v-model:visible="showProfileDrawer"
      position="right"
      :style="{ width: '28rem' }"
    >
      <template #header>
        <div>
          <h2 class="text-navy-800 text-base font-semibold">Edit Profile</h2>
          <p class="text-[13px] text-neutral-500">
            Update your name and email address.
          </p>
        </div>
      </template>

      <div class="space-y-4">
        <div>
          <label
            for="profile-name"
            class="text-navy-800 mb-1 block text-[13px] font-medium"
          >
            Name
          </label>
          <input
            id="profile-name"
            v-model="form.name"
            type="text"
            placeholder="Your name"
            class="text-navy-800 w-full rounded-md border border-neutral-300 px-3 py-[9px] text-[13px] focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none"
            :class="{ 'border-red-500': form.errors.name }"
          />
          <p v-if="form.errors.name" class="mt-1 text-xs text-red-600">
            {{ form.errors.name }}
          </p>
        </div>

        <div>
          <label
            for="profile-email"
            class="text-navy-800 mb-1 block text-[13px] font-medium"
          >
            Email
          </label>
          <input
            id="profile-email"
            v-model="form.email"
            type="email"
            placeholder="your@email.com"
            class="text-navy-800 w-full rounded-md border border-neutral-300 px-3 py-[9px] text-[13px] focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none"
            :class="{ 'border-red-500': form.errors.email }"
          />
          <p v-if="form.errors.email" class="mt-1 text-xs text-red-600">
            {{ form.errors.email }}
          </p>
          <p class="mt-1 text-[12px] text-neutral-400">
            Changing your email will require re-verification.
          </p>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-between">
          <button
            class="cursor-pointer border-none bg-transparent text-[13px] text-neutral-500"
            @click="cancelProfileEdit"
          >
            Cancel
          </button>
          <AppButton :loading="form.processing" @click="saveProfile">
            Save Changes
          </AppButton>
        </div>
      </template>
    </Drawer>

    <!-- Change Password Drawer -->
    <Drawer
      v-model:visible="showPasswordDrawer"
      position="right"
      :style="{ width: '28rem' }"
    >
      <template #header>
        <div>
          <h2 class="text-navy-800 text-base font-semibold">Change Password</h2>
          <p class="text-[13px] text-neutral-500">
            Enter your current password and choose a new one.
          </p>
        </div>
      </template>

      <div class="space-y-4">
        <div>
          <label
            for="current-password"
            class="text-navy-800 mb-1 block text-[13px] font-medium"
          >
            Current Password
          </label>
          <input
            id="current-password"
            v-model="passwordForm.current_password"
            type="password"
            placeholder="Enter current password"
            class="text-navy-800 w-full rounded-md border border-neutral-300 px-3 py-[9px] text-[13px] focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none"
            :class="{ 'border-red-500': passwordForm.errors.current_password }"
          />
          <p
            v-if="passwordForm.errors.current_password"
            class="mt-1 text-xs text-red-600"
          >
            {{ passwordForm.errors.current_password }}
          </p>
        </div>

        <div>
          <label
            for="new-password"
            class="text-navy-800 mb-1 block text-[13px] font-medium"
          >
            New Password
          </label>
          <input
            id="new-password"
            v-model="passwordForm.password"
            type="password"
            placeholder="Enter new password"
            class="text-navy-800 w-full rounded-md border border-neutral-300 px-3 py-[9px] text-[13px] focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none"
            :class="{ 'border-red-500': passwordForm.errors.password }"
          />
          <p
            v-if="passwordForm.errors.password"
            class="mt-1 text-xs text-red-600"
          >
            {{ passwordForm.errors.password }}
          </p>
          <p class="mt-1 text-[12px] text-neutral-400">
            Must be at least 12 characters.
          </p>
        </div>

        <div>
          <label
            for="confirm-password"
            class="text-navy-800 mb-1 block text-[13px] font-medium"
          >
            Confirm New Password
          </label>
          <input
            id="confirm-password"
            v-model="passwordForm.password_confirmation"
            type="password"
            placeholder="Confirm new password"
            class="text-navy-800 w-full rounded-md border border-neutral-300 px-3 py-[9px] text-[13px] focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none"
            :class="{
              'border-red-500': passwordForm.errors.password_confirmation,
            }"
          />
          <p
            v-if="passwordForm.errors.password_confirmation"
            class="mt-1 text-xs text-red-600"
          >
            {{ passwordForm.errors.password_confirmation }}
          </p>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-between">
          <button
            class="cursor-pointer border-none bg-transparent text-[13px] text-neutral-500"
            @click="cancelPasswordEdit"
          >
            Cancel
          </button>
          <AppButton :loading="passwordForm.processing" @click="savePassword">
            Update Password
          </AppButton>
        </div>
      </template>
    </Drawer>
  </div>
</template>

<script setup lang="ts">
import { Head, Link, useForm } from "@inertiajs/vue3"
import Drawer from "primevue/drawer"
import { computed, ref, watch } from "vue"

import AppButton from "../../components/ui/AppButton.vue"
import AppCard from "../../components/ui/AppCard.vue"

interface Props {
  user: {
    id: string
    name: string
    email: string
    mfa_enabled: boolean
  }
  backup_codes_remaining: number
}

const props = defineProps<Props>()

const backupCodesRemaining = computed(() => props.backup_codes_remaining)

const userInitials = computed(() => {
  const name = props.user.name || ""
  const parts = name.split(" ").filter(Boolean)
  if (parts.length >= 2) {
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
})

// Drawer visibility
const showProfileDrawer = ref(false)
const showPasswordDrawer = ref(false)

// Profile form
const form = useForm({
  name: props.user.name,
  email: props.user.email,
})

watch(showProfileDrawer, (isOpen) => {
  if (isOpen) {
    form.name = props.user.name
    form.email = props.user.email
    form.clearErrors()
  }
})

const cancelProfileEdit = () => {
  showProfileDrawer.value = false
  form.reset()
  form.clearErrors()
}

const saveProfile = () => {
  form.transform((data) => ({ user: data }))
  form.patch("/account", {
    onSuccess: () => {
      showProfileDrawer.value = false
    },
  })
}

// Password form
const passwordForm = useForm({
  current_password: "",
  password: "",
  password_confirmation: "",
})

watch(showPasswordDrawer, (isOpen) => {
  if (isOpen) {
    passwordForm.reset()
    passwordForm.clearErrors()
  }
})

const cancelPasswordEdit = () => {
  showPasswordDrawer.value = false
  passwordForm.reset()
  passwordForm.clearErrors()
}

const savePassword = () => {
  passwordForm.transform((data) => ({ user: data }))
  passwordForm.patch("/account/password", {
    onSuccess: () => {
      showPasswordDrawer.value = false
      passwordForm.reset()
    },
  })
}
</script>
